import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TablePagination,
  Grid,
  InputAdornment,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Menu,
  ListItemIcon,
} from '@mui/material';
import {
  Search,
  CheckCircle,
  Cancel,
  Visibility,
  CalendarToday,
  Person,
  AccountBalance,
  Receipt,
  Download,
  FilterList,
  PictureAsPdf,
  Description,
  Refresh,
} from '@mui/icons-material';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import Loader from '../../themes/useLoader';
import AlertModal from '../../components/AlertModal';
import { exportAsCSV, exportAsPDF } from '../../utils/importExport';
import { exportAsExcel } from '../../utils/importExport';
import ExportButton from '../../components/ExportButton/ExportButton';
import { hasWritePermission, hasReadPermission } from '../../utils/menuFilter';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import MoreFilters from '../../components/MoreFilters/MoreFilters';


const ReceiptApproval = () => {
  const history = useHistory();
  const canWriteReceipt = hasWritePermission('booking.receipt-approval') || hasWritePermission('plot-booking.receipt-approval');
  const canReadReceipt = hasReadPermission('booking.receipt-approval') || hasReadPermission('plot-booking.receipt-approval');
  const [alertModal, setAlertModal] = useState({ open: false, title: 'Alert', message: '' });

  const showAlert = (message, title = 'Alert') => {
    setAlertModal({ open: true, title, message });
  };

  const closeAlert = () => {
    setAlertModal({ open: false, title: 'Alert', message: '' });
  };

  const [searchQuery, setSearchQuery] = useState('');
  const [bookingIdFilter, setBookingIdFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paymentModeFilter, setPaymentModeFilter] = useState('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [receiptData, setReceiptData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedReceipt, setSelectedReceipt] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalRemark, setApprovalRemark] = useState('');
  const [approvalDate, setApprovalDate] = useState(moment().format('YYYY-MM-DD'));
  const [actionType, setActionType] = useState('approve'); // 'approve' or 'reject'
  const [approvalLoading, setApprovalLoading] = useState(false);

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const exportMenuOpen = Boolean(exportAnchorEl);

  const searchTimeoutRef = useRef(null);

  // More Filters State
  const [moreFiltersOpen, setMoreFiltersOpen] = useState(false);
  const [sortBy, setSortBy] = useState('');
  const [tempSortBy, setTempSortBy] = useState('');
  const [dateFilter, setDateFilter] = useState(null);
  const [tempDateFilter, setTempDateFilter] = useState(null);

  const handleApplyFilters = () => {
    setSortBy(tempSortBy);
    setDateFilter(tempDateFilter);
    setPage(0);
    setMoreFiltersOpen(false);
  };

  const handleResetFilters = () => {
    setSortBy('');
    setTempSortBy('');
    setDateFilter(null);
    setTempDateFilter(null);
    setPage(0);
  };

  // Fetch pending receipts from API
  const fetchReceipts = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');

      const params = new URLSearchParams({
        page: String(page + 1), // API is 1-indexed, MUI pagination is 0-indexed
        limit: String(rowsPerPage),
      });

      // Optional filters - only add if non-empty
      if (bookingIdFilter.trim()) {
        params.append('bookingId', bookingIdFilter.trim());
      }
      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }

      // Add Sorting Params
      if (sortBy) {
        let apiSortBy = '';
        let apiSortOrder = 'asc';

        if (sortBy === 'dateDesc') {
          apiSortBy = 'date';
          apiSortOrder = 'desc';
        } else if (sortBy === 'dateAsc') {
          apiSortBy = 'date';
          apiSortOrder = 'asc';
        } else if (sortBy === 'amountDesc') {
          apiSortBy = 'amount';
          apiSortOrder = 'desc';
        } else if (sortBy === 'amountAsc') {
          apiSortBy = 'amount';
          apiSortOrder = 'asc';
        }

        if (apiSortBy) {
          params.append('sortBy', apiSortBy);
          params.append('sortOrder', apiSortOrder);
        }
      }

      // Add Date Filter
      if (dateFilter) {
        params.append('date', dateFilter);
      }

      const response = await fetch(
        `${baseURL}/api/transactions/receipt/pending?${params.toString()}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();

      if (result.success) {
        setReceiptData(result.data || []);
        setTotalCount(result.total || 0);
      } else {
        setError(result.message || 'Failed to fetch receipts');
        setReceiptData([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error('Error fetching pending receipts:', err);
      setError('Failed to load pending receipts. Please try again.');
      setReceiptData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, bookingIdFilter, searchQuery, sortBy, dateFilter]);

  useEffect(() => {
    fetchReceipts();
  }, [fetchReceipts]);

  // Handle immediate input for controlled fields without triggering API immediately
  const [searchInput, setSearchInput] = useState('');
  const [bookingIdInput, setBookingIdInput] = useState('');

  const handleSearchChange = (value) => {
    setSearchInput(value);
  };

  const handleBookingIdChange = (value) => {
    setBookingIdInput(value);
  };

  const handleReset = () => {
    setSearchInput('');
    setSearchQuery('');
    setBookingIdInput('');
    setBookingIdFilter('');
    setPaymentModeFilter('all');
    setPage(0);
    handleResetFilters();
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
  };

  const handleManualSearch = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchQuery(searchInput);
    setBookingIdFilter(bookingIdInput);
    setPage(0);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleManualSearch();
    }
  };

  const handleOpenExportMenu = (event) => setExportAnchorEl(event.currentTarget);
  const handleCloseExportMenu = () => setExportAnchorEl(null);

  const handleView = (receipt) => {
    setSelectedReceipt(receipt);
    setViewDialogOpen(true);
  };

  const handleApprove = (receipt) => {
    setSelectedReceipt(receipt);
    setApprovalRemark('');
    setActionType('approve');
    setApprovalDialogOpen(true);
  };

  const handleReject = (receipt) => {
    setSelectedReceipt(receipt);
    setApprovalRemark('');
    setActionType('reject');
    setApprovalDialogOpen(true);
  };

  const handleSubmitApproval = async () => {
    setApprovalLoading(true);
    setError('');
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');

      const action = actionType === 'approve' ? 'approve' : 'cancel';
      const body = {
        receiptId: selectedReceipt._id,
        action: action,
        actionBy: localStorage.getItem('userName') || 'admin',
        approvalRemark: approvalRemark.trim(),
        newDate: approvalDate,
      };

      // Add cancelRemark if rejecting/cancelling
      if (action === 'cancel' && approvalRemark.trim()) {
        body.cancelRemark = approvalRemark.trim();
      }

      const response = await fetch(
        `${baseURL}/api/transactions/receipt/approve-or-cancel`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { Authorization: `Bearer ${token}` }),
          },
          body: JSON.stringify(body),
        }
      );

      const result = await response.json();

      if (result.success) {
        const actionText = actionType === 'approve' ? 'approved' : 'cancelled';
        showAlert(`Receipt REC-${selectedReceipt.receiptId} ${actionText} successfully!`);
        setApprovalDialogOpen(false);
        // Refresh data after action
        fetchReceipts();
      } else {
        showAlert(result.message || `Failed to ${actionType} receipt`, 'Error');
        setApprovalDialogOpen(false);
      }
    } catch (err) {
      console.error(`Error ${actionType}ing receipt:`, err);
      showAlert(`Failed to ${actionType} receipt. Please try again.`, 'Error');
      setApprovalDialogOpen(false);
    } finally {
      setApprovalLoading(false);
    }
  };

  const fetchAllForExport = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL;
      const token = localStorage.getItem('token');

      const fetchPage = async (pageNum) => {
        const params = new URLSearchParams({ page: String(pageNum) });
        if (bookingIdFilter.trim()) params.append('bookingId', bookingIdFilter.trim());
        if (searchQuery.trim()) params.append('search', searchQuery.trim());

        if (sortBy) {
          let apiSortBy = '';
          let apiSortOrder = 'asc';

          if (sortBy === 'dateDesc') {
            apiSortBy = 'date';
            apiSortOrder = 'desc';
          } else if (sortBy === 'dateAsc') {
            apiSortBy = 'date';
            apiSortOrder = 'asc';
          } else if (sortBy === 'amountDesc') {
            apiSortBy = 'amount';
            apiSortOrder = 'desc';
          } else if (sortBy === 'amountAsc') {
            apiSortBy = 'amount';
            apiSortOrder = 'asc';
          }

          if (apiSortBy) {
            params.append('sortBy', apiSortBy);
            params.append('sortOrder', apiSortOrder);
          }
        }
        if (dateFilter) {
          params.append('date', dateFilter);
        }

        const response = await fetch(
          `${baseURL}/api/transactions/receipt/pending?${params.toString()}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { Authorization: `Bearer ${token}` }),
            },
          }
        );
        return response.json();
      };

      // Extract docs from either { data: [...] } or { data: { docs: [...] } }
      const extractDocs = (result) => {
        if (Array.isArray(result.data)) return result.data;
        if (result.data?.docs && Array.isArray(result.data.docs)) return result.data.docs;
        return [];
      };

      // Step 1: Fetch page 1 using the API's own default page size (no limit override)
      const first = await fetchPage(1);
      if (!first.success) return [];

      const firstDocs = extractDocs(first);

      // Total number of records across all pages (from the API response)
      const total = first.total ?? first.data?.totalDocs ?? first.data?.total ?? firstDocs.length;

      // If all records already came back on page 1, we're done
      if (firstDocs.length === 0 || firstDocs.length >= total) return firstDocs;

      // Use the actual page size the API returned (not what we requested)
      // so we calculate the correct number of remaining pages
      const actualPageSize = firstDocs.length;
      const totalPages = Math.ceil(total / actualPageSize);

      // Step 2: Fetch all remaining pages sequentially to avoid hammering the server
      let allDocs = [...firstDocs];
      for (let p = 2; p <= totalPages; p++) {
        const result = await fetchPage(p);
        if (result.success) {
          allDocs = allDocs.concat(extractDocs(result));
        }
        // Stop early if we've collected enough
        if (allDocs.length >= total) break;
      }

      return allDocs;
    } catch (err) {
      console.error('Error fetching all data for export:', err);
      return [];
    }
  };

  const handleExportPDF = async () => {
    handleCloseExportMenu();
    setLoading(true);
    const data = await fetchAllForExport();
    setLoading(false);

    if (!data || data.length === 0) {
      showAlert('No data to export', 'Info');
      return;
    }

    const headers = [
      'Receipt ID',
      'Date',
      'Booking ID',
      'Customer',
      'Amount',
      'Mode',
      'Project',
      'Unit'
    ];

    const rows = data.map(receipt => ({
      'Receipt ID': `REC-${receipt.receiptId}`,
      'Date': moment(receipt.receivedDate).format('DD-MM-YYYY'),
      'Booking ID': receipt.bookingDetails?.bookingId || '-',
      'Customer': receipt.bookingDetails?.customer?.name || '-',
      'Amount': formatCurrency(receipt.amount),
      'Mode': receipt.mode || '-',
      'Project': receipt.bookingDetails?.particular?.projectName || '-',
      'Unit': `${receipt.bookingDetails?.particular?.inventoryType || ''} ${receipt.bookingDetails?.particular?.inventoryDetail || '-'}`
    }));

    exportAsPDF({
      rows,
      headers,
      title: 'Pending Receipts Report',
      fileNameBase: 'pending_receipts'
    });
  };

  const handleExportCSV = async () => {
    handleCloseExportMenu();
    setLoading(true);
    const data = await fetchAllForExport();
    setLoading(false);

    if (!data || data.length === 0) {
      showAlert('No data to export', 'Info');
      return;
    }

    const rows = data.map(receipt => ({
      'Receipt ID': `REC-${receipt.receiptId}`,
      'Date': moment(receipt.receivedDate).format('DD-MM-YYYY'),
      'Booking ID': receipt.bookingDetails?.bookingId || '-',
      'Customer': receipt.bookingDetails?.customer?.name || '-',
      'Mobile': receipt.bookingDetails?.customer?.phone || '-',
      'Amount': receipt.amount,
      'Status': receipt.receiptStatus || 'Pending',
      'Mode': receipt.mode || '-',
      'Ref No': receipt.refNo || '-',
      'Bank': receipt.bankName || '-',
      'Project': receipt.bookingDetails?.particular?.projectName || '-',
      'Inventory': receipt.bookingDetails?.particular?.inventoryType || '-',
      'Unit': receipt.bookingDetails?.particular?.inventoryDetail || '-',
      'Associate': receipt.bookingDetails?.associate?.name || '-'
    }));

    exportAsCSV({
      rows,
      fileNameBase: 'pending_receipts'
    });
  };

  const handleExportExcel = async () => {
    setLoading(true);
    const data = await fetchAllForExport();
    setLoading(false);

    if (!data || data.length === 0) {
      showAlert('No data to export', 'Info');
      return;
    }

    const rows = data.map(receipt => ({
      'Receipt ID': `REC-${receipt.receiptId}`,
      'Date': moment(receipt.receivedDate).format('DD-MM-YYYY'),
      'Booking ID': receipt.bookingDetails?.bookingId || '-',
      'Customer': receipt.bookingDetails?.customer?.name || '-',
      'Mobile': receipt.bookingDetails?.customer?.phone || '-',
      'Amount': receipt.amount,
      'Status': receipt.receiptStatus || 'Pending',
      'Mode': receipt.mode || '-',
      'Ref No': receipt.refNo || '-',
      'Bank': receipt.bankName || '-',
      'Project': receipt.bookingDetails?.particular?.projectName || '-',
      'Inventory': receipt.bookingDetails?.particular?.inventoryType || '-',
      'Unit': receipt.bookingDetails?.particular?.inventoryDetail || '-',
      'Associate': receipt.bookingDetails?.associate?.name || '-'
    }));

    exportAsExcel({
      rows,
      sheetName: 'Pending Receipts',
      fileNameBase: 'pending_receipts'
    });
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredReceiptData = paymentModeFilter === 'all'
    ? receiptData
    : receiptData.filter(receipt => {
        const mode = (receipt.mode || '').toLowerCase();
        return mode === paymentModeFilter.toLowerCase();
      });

  // Helper to format currency
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '₹0';
    return `₹${Number(amount).toLocaleString('en-IN')}`;
  };

  if (!canReadReceipt) {
    return (
      <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
        <Paper elevation={3} sx={{ p: 5, textAlign: 'center', borderRadius: 4, maxWidth: 500 }}>
          <Avatar sx={{ bgcolor: '#fee2e2', color: '#ef4444', width: 80, height: 80, mb: 3, mx: 'auto' }}>
            <Cancel sx={{ fontSize: 50 }} />
          </Avatar>
          <Typography variant="h4" fontWeight="bold" color="#1a202c" gutterBottom>
            Access Denied
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, fontSize: '1.1rem' }}>
            You do not have permission to view Receipt Approvals. Please contact your administrator.
          </Typography>
          <Button
            variant="contained"
            onClick={() => history.push('/app/dashboard')}
            sx={{
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3d5afe' },
              borderRadius: 2,
              px: 4,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold'
            }}
          >
            Back to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      {/* Header */}
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, justifyContent: 'space-between', alignItems: { xs: 'flex-start', md: 'center' }, mb: 4, gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 56, height: 56, bgcolor: '#536DFE' }}>
            <Receipt sx={{ fontSize: 32 }} />
          </Avatar>
          <Box>
            <Typography variant="h4" fontWeight="bold" color="primary">
              Receipt Approval Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Review and approve payment receipts • {totalCount} pending records
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, width: { xs: '100%', md: 'auto' }, justifyContent: { xs: 'flex-start', md: 'flex-end' }, alignItems: 'center' }}>
          {canWriteReceipt && (
            <ExportButton
              onExportPdf={handleExportPDF}
              onExportCsv={handleExportCSV}
              onExportExcel={handleExportExcel}
              variant="contained"
            />
          )}
          <RefreshButton onClick={fetchReceipts} loading={loading} />
        </Box>
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              placeholder="Search by receipt no, customer, plot..."
              value={searchInput}
              onChange={(e) => handleSearchChange(e.target.value)}
              onKeyPress={handleKeyPress}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search color="primary" />
                  </InputAdornment>
                ),
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <FormControl fullWidth>
              <InputLabel>Payment Mode</InputLabel>
              <Select
                value={paymentModeFilter}
                onChange={(e) => setPaymentModeFilter(e.target.value)}
                label="Payment Mode"
              >
                <MenuItem value="all">All Modes</MenuItem>
                <MenuItem value="Cash">Cash</MenuItem>
                <MenuItem value="Cheque">Cheque</MenuItem>
                <MenuItem value="RTGS/NEFT">RTGS/NEFT</MenuItem>
                <MenuItem value="UPI">UPI</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Button
                variant="outlined"
                startIcon={<Refresh />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: 1,
                  height: '56px',
                }}
                onClick={handleReset}
                color="inherit"
              >
                Reset
              </Button>
              <Button
                variant="contained"
                startIcon={<Search />}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  flex: 1,
                  height: '56px',
                }}
                onClick={handleManualSearch}
              >
                Search
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3} md={3}>
            <MoreFilters
              expanded={moreFiltersOpen}
              onToggle={() => setMoreFiltersOpen(!moreFiltersOpen)}
              sortBy={tempSortBy}
              onChangeSortBy={setTempSortBy}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
              type="receipt"
            >
              <Grid container spacing={1.5}>
        
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Search by Single Day"
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    value={tempDateFilter || ''}
                    onChange={(e) => setTempDateFilter(e.target.value || null)}
                  />
                </Grid>
              </Grid>
            </MoreFilters>
          </Grid>
        </Grid>
      </Paper>

      {/* Table */}
      <Paper sx={{ borderRadius: 3, boxShadow: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 700 }}>Receipt Info</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Booking / Customer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Property Info</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Amount Details</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Payment Method</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Associate</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <CircularProgress />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Loading receipts...
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredReceiptData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Typography variant="body2" color="text.secondary">No pending receipts found</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredReceiptData.map((receipt) => (
                  <TableRow key={receipt._id} sx={{ '&:hover': { backgroundColor: 'rgba(83, 109, 254, 0.04)' } }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                          <Receipt fontSize="small" />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="700">REC-{receipt.receiptId || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            <CalendarToday sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                            {receipt.receivedDate ? moment(receipt.receivedDate).format('DD-MM-YYYY') : '-'}
                          </Typography>
                          {receipt.receivedType && (
                            <Typography variant="caption" display="block" color="text.secondary">
                              {receipt.receivedType} • {receipt.paymentType ? receipt.paymentType.replace(/_/g, ' ') : ''}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="700" color="primary">
                          BKG-{receipt.bookingDetails?.bookingId || '-'}
                        </Typography>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                          {receipt.bookingDetails?.customer?.name || '-'}
                        </Typography>
                        {receipt.bookingDetails?.customer?.phone && (
                          <Typography variant="caption" color="text.secondary">
                            {receipt.bookingDetails.customer.phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="600">
                          <AccountBalance sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle', color: 'text.secondary' }} />
                          {receipt.bookingDetails?.particular?.inventoryType || 'Plot'} {receipt.bookingDetails?.particular?.inventoryDetail || '-'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {receipt.bookingDetails?.particular?.projectName || '-'}
                        </Typography>
                        {receipt.bookingDetails?.particular?.areaSize && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Area: {receipt.bookingDetails.particular.areaSize}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight="700">{formatCurrency(receipt.amount)}</Typography>
                      <Chip label={receipt.receiptStatus?.toUpperCase() || 'PENDING'} size="small" variant="outlined" sx={{ fontSize: '0.625rem', height: 16, mt: 0.5 }} />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={receipt.mode || '-'}
                        size="small"
                        color={receipt.mode === 'Cash' ? 'success' : 'info'}
                        variant="outlined"
                        sx={{ fontWeight: 600, mb: 0.5 }}
                      />
                      {receipt.refNo && (
                        <Typography variant="caption" display="block" color="text.secondary">Ref: {receipt.refNo}</Typography>
                      )}
                      {receipt.bankName && (
                        <Typography variant="caption" display="block" color="text.secondary">Bank: {receipt.bankName}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="600" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Person sx={{ fontSize: 14, color: 'text.secondary' }} />
                          {receipt.bookingDetails?.associate?.name || '-'}
                        </Typography>
                        {receipt.bookingDetails?.associate?.phone && (
                          <Typography variant="caption" color="text.secondary">
                            {receipt.bookingDetails.associate.phone}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton size="small" onClick={() => handleView(receipt)} sx={{ color: 'info.main' }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {canWriteReceipt && (
                          <Tooltip title="Approve">
                            <IconButton size="small" onClick={() => handleApprove(receipt)} sx={{ color: 'success.main' }}>
                              <CheckCircle fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {canWriteReceipt && (
                          <Tooltip title="Reject">
                            <IconButton size="small" onClick={() => handleReject(receipt)} sx={{ color: 'error.main' }}>
                              <Cancel fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          Receipt Details - REC-{selectedReceipt?.receiptId}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {selectedReceipt && (
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Receipt Number</Typography>
                <Typography variant="body1" fontWeight="600">REC-{selectedReceipt.receiptId}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Date</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedReceipt.receivedDate ? moment(selectedReceipt.receivedDate).format('DD-MM-YYYY') : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Booking ID</Typography>
                <Typography variant="body1" fontWeight="600" color="primary">
                  Booking ID-{selectedReceipt.bookingDetails?.bookingId || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Customer Name</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedReceipt.bookingDetails?.customer?.name || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Phone</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedReceipt.bookingDetails?.customer?.phone || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Project</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedReceipt.bookingDetails?.particular?.projectName || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Inventory / Unit No.</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedReceipt.bookingDetails?.particular?.inventoryType || ''} {selectedReceipt.bookingDetails?.particular?.inventoryDetail || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Area</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedReceipt.bookingDetails?.particular?.areaSize || '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Amount</Typography>
                <Typography variant="body1" fontWeight="700" color="success.main">
                  {formatCurrency(selectedReceipt.amount)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Payment Mode</Typography>
                <Typography variant="body1" fontWeight="600">{selectedReceipt.mode || '-'}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Payment Type</Typography>
                <Typography variant="body1" fontWeight="600">
                  {selectedReceipt.paymentType ? selectedReceipt.paymentType.replace(/_/g, ' ') : '-'}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Received Type</Typography>
                <Typography variant="body1" fontWeight="600">{selectedReceipt.receivedType || '-'}</Typography>
              </Grid>
              {selectedReceipt.refNo && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Reference No.</Typography>
                  <Typography variant="body1" fontWeight="600">{selectedReceipt.refNo}</Typography>
                </Grid>
              )}
              {selectedReceipt.bankName && (
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Bank Name</Typography>
                  <Typography variant="body1" fontWeight="600">{selectedReceipt.bankName}</Typography>
                </Grid>
              )}

              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">Created By</Typography>
                <Typography variant="body1" fontWeight="600">{selectedReceipt.createdBy || '-'}</Typography>
              </Grid>
              {selectedReceipt.otherRemark && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Remark</Typography>
                  <Typography variant="body1" fontWeight="600">{selectedReceipt.otherRemark}</Typography>
                </Grid>
              )}
              {selectedReceipt.bookingDetails?.associate?.name && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Associate</Typography>
                  <Typography variant="body1" fontWeight="600">
                    {selectedReceipt.bookingDetails.associate.name} ({selectedReceipt.bookingDetails.associate.phone || '-'})
                  </Typography>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          {canWriteReceipt && (
            <Button variant="contained" color="success" onClick={() => { setViewDialogOpen(false); handleApprove(selectedReceipt); }}>Approve</Button>)}
        </DialogActions>
      </Dialog>

      {/* Approval/Rejection Dialog */}
      <Dialog open={approvalDialogOpen} onClose={() => setApprovalDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {actionType === 'approve' ? <CheckCircle color="success" /> : <Cancel color="error" />}
          {actionType === 'approve' ? 'Approve Receipt' : 'Reject Receipt'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Are you sure you want to {actionType} receipt <strong>REC-{selectedReceipt?.receiptId}</strong> for {formatCurrency(selectedReceipt?.amount)}?
          </Typography>
          <TextField
            label="Remark (Optional)"
            fullWidth
            multiline
            rows={3}
            value={approvalRemark}
            onChange={(e) => setApprovalRemark(e.target.value)}
            placeholder={`Enter reason for ${actionType}...`}
            sx={{ mt: 1 }}
          />
          <TextField
            label={actionType === 'approve' ? "Approval Date" : "Rejection Date"}
            type="date"
            fullWidth
            value={approvalDate}
            onChange={(e) => setApprovalDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ min: moment().format('YYYY-MM-DD') }}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button onClick={() => setApprovalDialogOpen(false)} disabled={approvalLoading}>Cancel</Button>
          {canWriteReceipt && (
            <Button
              variant="contained"
              color={actionType === 'approve' ? 'success' : 'error'}
              onClick={handleSubmitApproval}
              disabled={approvalLoading}
              startIcon={approvalLoading && <CircularProgress size={20} color="inherit" />}
            >
              {actionType === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <AlertModal open={alertModal.open} title={alertModal.title} message={alertModal.message} onClose={closeAlert} />
    </Box>
  );
};

export default ReceiptApproval;
